import * as React from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { IPaymentMethod, IPolicyApplication, IPremium, IProduct } from '../../../../../data/api/models';
import { FormattedNumber } from 'react-intl';
import { capitalize, find, flow, get, head, isEmpty, startCase, toLower } from 'lodash';
import {
  change,
  destroy,
  formValueSelector,
  getFormSyncErrors,
  getFormValues,
  InjectedFormProps,
  reduxForm,
  reset
} from 'redux-form';
import { connect } from 'react-redux';
import { IStore } from '../../../../../redux/IStore';
import { DocumentsLinkButton } from '../../Components/DocumentsLinkButton';
import { FaSpinner } from 'react-icons/fa';
import { IQuoteActions, IQuoteState as IQuoteServiceState } from '../../../../../services/quote/models';
import { IBrandingActions, IBrandingState, II18nMessages } from '../../../../../services/branding/models';
import ChangeRegion from '../components/ChangeRegion';
import { IAuthState } from '../../../../../services/auth/models';
import { IVeriskState } from '../../../../../services/verisk/models';
import ChangeTier from '../components/ChangeTier';
import ChangeTravellers from '../components/ChangeTravellers';
import Signposting, { SignpostType } from '../../Components/Signposting';
import { OptionsDisplay } from '../../Components/OptionsDisplay';
import ChangeAdditionalOptions from '../components/ChangeAdditionalOptions';
import AddPaymentCard from '../../../../../components/AddPaymentCard';
import PaymentWithCard from '../../../../../components/PaymentWithCard';
import CancelRenewalModal from '../../CancelRenewalModal';
import { actions as authActions, IAuthActions } from '../../../../../services/auth/reducer';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import { actions as quoteActions } from '../../../../../services/quote/reducer';
import { actions as brandingActions } from '../../../../../services/branding/reducer';
import { toastr } from 'react-redux-toastr';
import ChangeMaxTrip from '../components/ChangeMaxTrip';
import { RenewNow } from '../components/RenewNow';
import { Spinner } from '../../Components/Spinner';
import { IconCircle } from '../../../../../components/Icons/IconCircle';
import { GoBack } from '../../Components/GoBack';
import { ICustomerMTAOptions } from '../../MakeAChange';
import RenewalCancelButton from '../components/RenewalCancelButton';
import { format, parse, subDays } from 'date-fns';
import { WhatsCovered } from '../../Components/WhatsCovered';
import { DiscountCodeButton } from '../components/DiscountCodeButton';

export interface IRenewalProps extends InjectedFormProps {
  auth: IAuthState;
  formValues: IPolicyApplication;
  initialValues: IPolicyApplication;
  dispatch: any;
  change: any;
  history: any;
  documentsUrl: string;
  endDate: string;
  startDate: string;
  destinationsValue: string[];
  stateCitiesValue: string[];
  premiums: any[];
  quote: IQuoteServiceState;
  region: string;
  product: IProduct;
  i18n: II18nMessages;
  verisk: IVeriskState;
  actions: {
    auth: IAuthActions;
    branding: IBrandingActions;
    quote: IQuoteActions;
    calculate: any;
    push(path: string);
    destroy(formName: string);
    change: (form, field, value) => void;
  };
  synchronousError: any;
  branding: IBrandingState;
  customerMTAOptions: ICustomerMTAOptions;
  match: any;
  reInitRenewal?: () => any;
  errorMessage: string;
}

export interface RenewalFormState {
  editMode: boolean;
  type?: string;
  customerLapsed: boolean;
  loading: boolean;
  hidePaymentButton: boolean;
}

const DATE_FORMAT = 'dd/MM/yyyy';

class RenewalForm extends React.Component<IRenewalProps, RenewalFormState> {

  constructor(props) {
    super(props);
    const { actions, product, initialValues, dispatch, change } = props;

    this.state = {
      editMode: false,
      type: null,
      customerLapsed: initialValues && initialValues.status === 'customer lapsed',
      loading: true,
      hidePaymentButton: false,
    }
    this.editRenewalHandler = this.editRenewalHandler.bind(this)
    const selectedSchemeId = get(initialValues, 'schemeId');
    const premiums = get(initialValues, 'premiums', [])

    if (premiums) {
      const premium = head(premiums);
      if (get(premium, 'referrals', []).length > 0) {
        this.returnToRenewalsSummary()
      }
    }

    if (selectedSchemeId) {
      Promise.resolve(actions.quote.calculateQuote(product.id, true)).then((data) => {
        Promise.resolve(dispatch(change('signposted', {
          medicalLoad: data?.isSignposted || false,
          medicalDecline: data?.isMedical || false
        }))).then(() => {
          this.setState({ loading: false });
        })
      })
    }
  }

  componentDidMount() {
    const { match } = this.props;
    if (get(match, 'params.params') === 'region') {
      this.setState({
        editMode: true,
        type: 'region'
      })
    }

    if (get(match, 'params.params') === 'tier') {
      this.setState({
        editMode: true,
        type: 'tier'
      })
    }
  }

  componentDidUpdate(prevProps: Readonly<IRenewalProps>, prevState: Readonly<RenewalFormState>, snapshot?: any) {
    const { quote, dispatch, change, formValues } = this.props;
    const newPremium = get(quote, 'application.premiums');
    const oldPremium = get(formValues, 'premiums');
    if (newPremium && oldPremium && newPremium !== oldPremium) {
      // TODO: set a flag when a premium is in referral state
      dispatch(change('premiums', newPremium))
    }
  }

  protected unlapseRenewal = (selectedQuote: IPolicyApplication) => () => {
    const { actions } = this.props;

    if (!selectedQuote) {
      return;
    }

    this.setState({ loading: true })

    actions.quote.unlapseRenewal(selectedQuote.id).then(() => {
      this.setState({ customerLapsed: false });
      Promise.resolve(actions.auth.reloadCustomer()).then(() => {
        actions.push('/account/summary')
        this.setState({ loading: false })
      });
    });
  }

  private returnToRenewalsSummary = () => {
    const { actions } = this.props;
    actions.push('/account/summary')
  }

  public toggleCustomerLapsed = () => {
    const { actions } = this.props;
    this.setState({ customerLapsed: !this.state.customerLapsed });
    actions.push('/account/summary')
  }

  private isSubmittingChange = (): boolean => {
    const { quote } = this.props;
    return get(quote, 'isSubmitting', false)
  }

  private handleSubmitForm = () => {
    const { actions, dispatch, change, product } = this.props;
    dispatch(change('generateDocuments', true))

    Promise.resolve(actions.quote.calculateQuote(product.id, true)).then((data) => {
      if (get(data, 'isSignposted', false)) {
        const isMedicalDecline = get(data, 'isMedical', false);
        Promise.resolve(dispatch(change('signposted', {
          medicalLoad: !isMedicalDecline,
          medicalDecline: isMedicalDecline
        }))).then(() => {
          Promise.resolve(actions.quote.saveQuote(true)).then((applicationId) => {
            if (isMedicalDecline) {
              actions.push('/account/renewals/medical-decline')
            } else {
              this.setState({ editMode: false, type: null });
              Promise.resolve(actions.auth.reloadCustomer()).then(() => {
                actions.push(`/account/renewals/${applicationId}`)
                toastr.success('Your Renewal has been Updated');
              })
            }
          });
        })
      } else if (get(data, 'hasReferralRule', false)) {
        Promise.resolve(actions.quote.saveQuote(true)).then(() => {
          actions.push('/account/renewals/call-centre')
        });
      } else {
        Promise.resolve(actions.quote.saveQuote(true)).then((applicationId) => {
          Promise.resolve(dispatch(change('signposted', {
            medicalLoad: false,
            medicalDecline: false
          }))).then(() => {
            this.setState({ editMode: false, type: null });
            Promise.resolve(actions.auth.reloadCustomer()).then(() => {
              actions.push(`/account/renewals/${applicationId}`)
              toastr.success('Your Renewal has been Updated');
            })
          });
        });
      }
    })
  }
  private renderCardOptionButton = (activePaymentMethod: IPaymentMethod) => {
    const { formValues } = this.props;
    const { customerLapsed, hidePaymentButton } = this.state;

    if (customerLapsed) {
      return null;
    }

    const isRenewalStatusManual = get(formValues, 'renewalStatus', '') === 'manual';

    if (isRenewalStatusManual) {
      return (
        <>
          {!hidePaymentButton && (<div>{this.renderManualRenewalCard()}</div>)}
        </>
      )
    }

    return (
      <>
        {!hidePaymentButton && (<div>{this.renderAutoRenewalCard(activePaymentMethod)}</div>)}
      </>
    )

  }

  private renderManualRenewalCard = () => {

    const { quote, auth, formValues } = this.props;
    const title = 'RENEW NOW'

    return (
      <PaymentWithCard
        classStyle={'secondary-button'}
        buttonTitle={title}
        buttonDisabled={false}
        showSpinner={false}
        isBlock={true}
        travellers={formValues.travellers}
        customerId={auth.customer.id}
        policyId={quote.application.renewingPolicyId}
      />
    )

  }

  private renderAutoRenewalCard = (activePaymentMethod: IPaymentMethod) => {
    const { quote, auth, formValues } = this.props;
    const { editMode } = this.state;
    let title = 'ADD CARD';
    if (activePaymentMethod.id) {
      title = 'UPDATE CARD'
    }

    return (
      <AddPaymentCard
        buttonTitle={title}
        classStyle={'secondary-button'}
        buttonDisabled={editMode || this.isSubmittingChange()}
        showSpinner={false}
        isBlock={true}
        travellers={formValues.travellers}
        customerId={auth.customer.id}
        policyId={quote.application.renewingPolicyId}
      />
    );

  }

  private checkHasDiscount = (premium: IPremium): boolean => {
    const campaigns = get(premium, 'campaigns', []) || [];
    return (campaigns.length > 0) && get(premium, 'discount', 0) < 0;
  }

  private handleHidePurchaseButton = (shouldHide: boolean) => {
    this.setState({ hidePaymentButton: shouldHide })
  }

  private renderPremiumInfo = () => {
    const { formValues, auth, actions, quote, dispatch } = this.props;

    const premiums = get(formValues, 'premiums', []);
    if (!premiums.length) {
      return null;
    }

    const isAutoRenewal = get(formValues, 'renewalStatus', '') === 'auto';
    const assignedPaymentMethodId = get(formValues, 'assignedPaymentMethods.id');
    const paymentMethods = get(auth, 'customer.paymentMethods');
    let currentPaymentMethod = null;

    if (assignedPaymentMethodId) {
      if (!isEmpty(paymentMethods)) {
        currentPaymentMethod = find(paymentMethods, ['id', assignedPaymentMethodId])
      }
    }

    return (
      <>
        <div>
          {isAutoRenewal && (
            <RenewNow
              startDate={formValues.startDate}
              assignedPaymentMethod={currentPaymentMethod}
              quoteReference={formValues.quoteReference}
              region={formValues.region}
              actions={actions}
              travellers={formValues.travellers}
              policyId={quote.application.renewingPolicyId}
              customerId={auth.customer.id}
              dispatch={dispatch}
              customerPaymentMethods={paymentMethods}
              hidePurchaseCardButton={this.handleHidePurchaseButton}
            />
          )}
        </div>
      </>
    )
  }

  protected renderDestinations = (region): string => {
    if (region) {
      const regionParse = region === 'uk' ? 'United Kingdom' : region;
      return startCase(toLower(regionParse));
    }

    return '';
  }

  private editRenewalHandler = (type: string) => () => {
    this.setState({
      editMode: true,
      type
    })
  }

  private renderEditButton = (text: string, type: string) => {
    const { editMode, customerLapsed } = this.state;

    if (customerLapsed) {
      return null;
    }

    const isEditRegion = text === 'Edit Region';

    return (
      <Button
        bsStyle="primary"
        className="secondary-button"
        block={true}
        id={isEditRegion ? 'edit-region-button' : null}
        onClick={this.editRenewalHandler(type)}
        disabled={editMode || this.isSubmittingChange()}>
        {this.isSubmittingChange() && (<FaSpinner className="fa-spin"/>)}{text}
      </Button>
    )
  }

  protected renderTravellersTable = () => {
    const { auth, formValues } = this.props;
    const customer = auth.customer;
    const travellers = formValues.travellers;

    const leadTraveller = travellers[0];
    const editButton = this.renderEditButton('EDIT TRAVELLERS & MEDICAL', 'travellers');
    const leadTravellerConditions = get(leadTraveller, 'screening.meta.conditions', null);

    // If its a renewal we set the auth customer details not the one from the metadata
    if (customer && get(formValues, 'quoteType', '') === 'renewal') {
      leadTraveller.address = customer.address;
      leadTraveller.email = customer.email;
      leadTraveller.phone = customer.phone1;
    }

    const leadName = leadTraveller.title + ' ' + leadTraveller.firstName + ' ' + leadTraveller.lastName;

    return (
      <>
        <div className={'details-overview'}>
          <div className={'details-overview-header wrap'}>
            <div className={'d-overview-icon flex'}>
              <div className={'d-overview-icon-buttons'}>
                <img src={require('../../MakeAChange/Images/user.svg')} width={'50px'}/>
                <div className={'d-overview-text'}>Travellers</div>
              </div>
            </div>
            <div className={'d-overview-button-wrapper flex'}>
              <div>{editButton}</div>
            </div>
          </div>

          <div className={'details-overview-row'}>
            <div className={'d-o-attribute'}>
              <div>{leadName}</div>
              <div className={'d-o-attribute-value'}>{leadTraveller.dob}</div>
              <div
                className={'d-o-attribute-value'}>{!isEmpty(leadTravellerConditions) ? leadTravellerConditions.join(', ') : 'No Medical Conditions'}</div>
            </div>
          </div>

          {travellers.length > 1 &&
            travellers.slice(1).map((traveller, index) => {
              const travellerConditions = get(traveller, 'screening.meta.conditions');
              const travellerName = traveller.title + ' ' + traveller.firstName + ' ' + traveller.lastName;
              return (
                <div className={'details-overview-row'} key={index}>
                  <div className={'d-o-attribute'}>
                    <div>{travellerName}</div>
                    <div className={'d-o-attribute-value'}>{traveller.dob}</div>
                    <div
                      className={'d-o-attribute-value'}>{!isEmpty(travellerConditions) ? travellerConditions.join(', ') : 'No Medical Conditions'}</div>
                  </div>
                </div>
              )
            })
          }
        </div>
      </>
    );
  }

  protected renderExtrasTable = () => {
    const { formValues } = this.props;
    const options = get(formValues, 'options', []);
    const editButton = this.renderEditButton('EDIT ADDITIONAL COVER', 'additional');

    return (
      <>
        <div className={'details-overview'}>
          <div className={'details-overview-header wrap'}>
            <div className={'d-overview-icon flex'}>
              <div className={'d-overview-icon-buttons'}>
                <img src={require('../../MakeAChange/Images/add-button.svg')} width={'50px'}/>
                <div className={'d-overview-text'} style={{ marginLeft: '10px' }}>Additional Cover</div>
              </div>
            </div>
            <div className={'d-overview-button-wrapper flex'}>
              <div>{editButton}</div>
            </div>
          </div>

          <div className={'details-overview-row'}>
            <div className={'d-o-attribute responsive-wrap'}>
              <div><OptionsDisplay options={options} optionName={'Winter Sports'}/></div>
            </div>
          </div>

          <div className={'details-overview-row'}>
            <div className={'d-o-attribute responsive-wrap'}>
              <div className={'d-o-attribute-value'}><OptionsDisplay options={options} optionName={'Cruise Cover'}/>
              </div>
            </div>
          </div>

          <div className={'details-overview-row'}>
            <div className={'d-o-attribute responsive-wrap'}>
              <div><OptionsDisplay options={options} optionName={'Excess Waiver'}/></div>
            </div>
          </div>

          <div className={'details-overview-row'}>
            <div className={'d-o-attribute responsive-wrap'}>
              <div className={'d-o-attribute-value'}><OptionsDisplay options={options} optionName={'Gadget Cover'}/>
              </div>
            </div>
          </div>

          <div className={'details-overview-row'}>
            <div className={'d-o-attribute'}>
              <div><OptionsDisplay options={options} optionName={'Hazardous Activities'}/></div>
            </div>
          </div>

          <div className={'details-overview-row'}>
            <div className={'d-o-attribute'}>
              <div><OptionsDisplay options={options} optionName={'Enhanced Trip Disruption Cover'}/></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  private renderDiscountCodeButton = () => {

    const { editMode } = this.state;
    return (
      <DiscountCodeButton
        disabled={editMode || this.isSubmittingChange()}
        loading={this.isSubmittingChange()}
        props={this.props}
        isBlock={true}
        classStyle={'secondary-button'}
        callback={this.handleSubmitForm}
      />
    )
  }

  private renderManualRenewalInfo = () => {
    const { formValues } = this.props;
    const expiresAt = format(subDays(parse(formValues.startDate, 'dd/MM/yyyy', new Date()), 1), 'dd/MM/yyyy');
    return (
      <>
        <div><b>Your policy will expire on {expiresAt}.</b></div>
        <div className={'mt-15'}>
          Please take time to review your renewal to ensure all the details are correct and the cover still meets
          your needs, You can make changes to your quote and buy once you’re happy with the cover selected.
        </div>

      </>
    )
  }

  private renderRenewalHeader = () => {
    const { editMode } = this.state;
    const { formValues, documentsUrl } = this.props

    const premiums = get(formValues, 'premiums', []);
    const activePaymentMethod = get(formValues, 'activePaymentMethod');
    const referenceText = `Quote Reference ${formValues.quoteReference}`
    const grossPremium = head(premiums)?.gross
    const hasDiscount = this.checkHasDiscount(head(premiums));
    let premium;

    if (premiums && premiums.length === 1) {
      premium = head(premiums);
    }

    const coverLevel = premium?.scheme?.coverLevel
    const isAutoRenewal = get(formValues, 'renewalStatus', '') === 'auto';

    return (
      <div className={'renewal-edit-header-container'}>
        <div className={'r-e-header'}>
          <div className={'r-h-icon'}>
            <IconCircle iconName={'plane-annual.svg'} color={'blue'}/>
          </div>
          <div className={'r-h-text'}>
            <div className={'premium-info'}>
              {'Your Renewal Premium: '}<span>
                      <FormattedNumber
                        value={grossPremium}
                        style="currency"
                        currency="GBP"
                      /></span>
            </div>
            {hasDiscount && (
              <>
                <span
                  style={{ textDecoration: 'line-through' }}>{'Your quoted price before discount £' + get(head(premiums), 'premiumBeforeAdjustment.gross', 0.00).toFixed(2)}</span>
                <span className={'r-h-discount'}>DISCOUNT APPLIED</span>
              </>
            )}
          </div>
        </div>

        <div className={'r-e-h-body'}>
          <div className={'r-e-h-b-text'}>
            <div className={'primary-text'}>{'Annual Multi trip - ' + capitalize(coverLevel)}</div>
            <div className={'primary-text'}>{referenceText}</div>
            {isAutoRenewal && <div style={{ marginTop: '15px' }}>{this.renderPremiumInfo()}</div>}
            {!isAutoRenewal && <div style={{ marginTop: '15px' }}>{this.renderManualRenewalInfo()}</div>}
          </div>
          <div className={'r-e-h-buttons'}>
            <div>
              <DocumentsLinkButton
                renewal={true}
                isBlock={true}
                documentsUrl={documentsUrl}
                isLoading={this.isSubmittingChange() || editMode}
              />
            </div>
            <div style={{ margin: '15px 0 15px 0' }}>
              {this.renderCardOptionButton(activePaymentMethod)}
            </div>
            <div>{this.renderDiscountCodeButton()}</div>
          </div>
        </div>
      </div>
    )
  }

  private renderCoverDetails = () => {
    const { type } = this.state;
    const { formValues, region } = this.props;

    let tier = '';
    const startDate = get(formValues, 'startDate');
    const endDate = get(formValues, 'endDate');
    const endDateToDisplay = endDate ? format(parse(endDate, DATE_FORMAT, new Date()), DATE_FORMAT) : '';
    const maxTripDuration = get(formValues, 'maxTripDuration');
    const premiums = get(formValues, 'premiums', []);

    let allowMaxTripChange = false;
    let premium;

    if (premiums && premiums.length === 1) {
      premium = head(premiums);
      tier = startCase(toLower(get(premium, 'scheme.coverLevel')));
      allowMaxTripChange = parseInt(get(premium, 'information.upsellMaxTripDuration', 0), 10) === 60;
    }

    return (
      <div className={'details-overview'}>
        <div className={'details-overview-header'}>
          <div className={'d-overview-icon'}>
            <img src={require('../../MakeAChange/Images/clipboard.svg')} width={'50px'} alt={''}/>
          </div>
          <div className={'d-overview-text'}>Your Cover Details</div>
        </div>

        <div className={'details-overview-row'}>
          <div className={'d-o-attribute'}>
            <div className={'d-o-attribute-title'}>Start Date</div>
            <div className={'d-o-attribute-value'}>{startDate}</div>
            <div className={'d-o-attribute-value hide-mobile'}/>
          </div>
        </div>
        <div className={'details-overview-row'}>
          <div className={'d-o-attribute'}>
            <div className={'d-o-attribute-title'}>End Date</div>
            <div className={'d-o-attribute-value'}>{endDateToDisplay}</div>
            <div className={'d-o-attribute-value hide-mobile'}/>
          </div>
        </div>
        {type !== 'tier' && <div className={'details-overview-row'}>
          <div className={'d-o-attribute responsive-wrap'}>
            <div className={'d-o-attribute-title'}>Tier</div>
            <div className={'d-o-attribute-value'}>{tier}</div>
            <div className={'d-o-attribute-button'}>{this.renderEditButton('EDIT TIER', 'tier')}</div>
          </div>
        </div>}
        {type !== 'region' && <div className={'details-overview-row'}>
          <div className={'d-o-attribute responsive-wrap'}>
            <div className={'d-o-attribute-title'}>Destination</div>
            <div className={'d-o-attribute-value'}>{this.renderDestinations(region)}</div>
            <div className={'d-o-attribute-button'}>{this.renderEditButton('EDIT REGION', 'region')}</div>
          </div>
        </div>}

        {allowMaxTripChange && type !== 'maxTrip' && (
          <div className={'details-overview-row'}>
            <div className={'d-o-attribute responsive-wrap'}>
              <div className={'d-o-attribute-title'}>Maximum individual trip length</div>
              <div className={'d-o-attribute-value'}>{maxTripDuration + ' days'}</div>
              <div
                className={'d-o-attribute-button'}>{this.renderEditButton('EDIT MAX TRIP', 'maxTrip')}</div>
            </div>
          </div>
        )}
      </div>
    )
  }

  private initRenewal = () => {
    this.props.reInitRenewal()
  }

  private reInitRenewal = (loading: boolean) => {
    return (
      <Button
        bsStyle="primary"
        type="button"
        onClick={this.initRenewal}
        disabled={loading}
        className="medium-width block secondary-button"
      >
        GO BACK
      </Button>
    )
  }

  private contactUs = () => {
    window.open('https://www.admiral.com/contact-us/travel', '_blank')
  }

  private renderGatewayErrorMessage = () => {
    const { formValues } = this.props;
    return (
      <Row>
        <Col>
          <p><b>We were unable to process your payment request.</b></p>
          <p>No payment has been taken and no insurance cover is in place.</p>
          <p>If you experience any further problems, please <span
            onClick={this.contactUs} className={'link-element'}>contact us</span> so we can help you further.
          </p>
          <p>Your quote details have been saved – quote Ref {formValues?.quoteReference}</p>
        </Col>
      </Row>
    )
  }

  private renderErrorMessage = () => {
    const { errorMessage } = this.props;

    if (!errorMessage) {
      return null;
    }

    if (errorMessage !== 'Payment Error') {
      return (
        <div className="error-block-container">
          {this.renderGatewayErrorMessage()}
        </div>
      )
    }

    return null;

  }

  public render() {
    const { editMode, type, loading } = this.state;
    const {
      handleSubmit,
      i18n,
      product,
      formValues,
      actions,
      verisk,
      dispatch,
      change,
      history,
      quote,
      region,
      branding,
      synchronousError,
      touch,
      customerMTAOptions,
    } = this.props;

    if (!formValues) {
      return null;
    }

    let premium;
    let allowMaxTripChange = false;

    const isMobile = window.innerWidth <= 600;
    const premiums = get(formValues, 'premiums', []);
    const medicalSignpost = get(formValues, 'signposted.medicalLoad', false);
    const showCancelRenewal = get(customerMTAOptions, 'allow_cancel_renewal.enabled', false);
    const goBackUrl = '/account/summary';

    if (premiums && premiums.length === 1) {
      premium = head(premiums);
      allowMaxTripChange = parseInt(get(premium, 'information.upsellMaxTripDuration', 0), 10) === 60;
    }

    const controlGroup = get(premium, 'information.controlGroup')

    if (loading) {
      return (
        <div style={{ marginTop: '15px' }}>
          <Spinner text={'Loading your Renewal...'}/>
        </div>
      )
    }

    return (
      <div>
        <Form horizontal={true} onSubmit={handleSubmit(this.handleSubmitForm)} autoComplete="off">

          <>
            <div style={{ marginTop: '15px' }}/>
            {this.renderErrorMessage()}
          </>

          <>
            <div style={{ marginTop: '15px' }}/>
            {this.renderRenewalHeader()}
          </>

          {medicalSignpost && (
            <>
              <div style={{ marginTop: '15px' }}/>
              <Signposting type={SignpostType.TYPE_A}/>
            </>
          )}
          <>
            {!editMode && (
              <>
                <div style={{ marginTop: '15px' }}/>
                {this.renderCoverDetails()}
              </>
            )}

            {!editMode && type !== 'travellers' && (
              <>
                <div style={{ marginTop: '15px' }}/>
                {this.renderTravellersTable()}
              </>
            )}

            {!editMode && type !== 'additional' && (
              <>
                <div style={{ marginTop: '15px' }}/>
                {this.renderExtrasTable()}
              </>
            )}

            {editMode && type === 'region' && (
              <Row>
                <Col xs={12}>
                  <ChangeRegion
                    product={product}
                    dispatch={dispatch}
                    change={change}
                    region={region}
                    actions={actions}
                    loading={verisk.isLoading || quote.isSubmitting || quote.isCalculating}
                    onSubmit={this.handleSubmitForm}
                    goBackButton={this.reInitRenewal(verisk.isLoading || quote.isSubmitting || quote.isCalculating)}
                    i18n={i18n}/>
                </Col>
              </Row>
            )}

            {allowMaxTripChange && editMode && type === 'maxTrip' && (
              <Row>
                <Col xs={12}>
                  <ChangeMaxTrip
                    product={product}
                    goBackButton={this.reInitRenewal(verisk.isLoading || quote.isSubmitting || quote.isCalculating)}
                    loading={quote.isSubmitting || quote.isCalculating}/>
                </Col>
              </Row>
            )}

            {editMode && type === 'tier' && (
              <ChangeTier
                product={product}
                dispatch={dispatch}
                change={change}
                actions={actions}
                quote={quote}
                formValues={formValues}
                isLoading={quote.isCalculating || quote.isSubmitting}
                goBackButton={this.reInitRenewal(verisk.isLoading || quote.isSubmitting || quote.isCalculating)}
                i18n={i18n}/>
            )}

            {editMode && type === 'travellers' && (
              <Row>
                <Col xs={12}>
                  <ChangeTravellers
                    handleSubmitForm={this.handleSubmitForm}
                    change={change}
                    actions={actions}
                    dispatch={dispatch}
                    branding={branding}
                    touch={touch}
                    formValues={formValues}
                    verisk={verisk}
                    synchronousError={synchronousError}
                    isLoading={quote.isCalculating || quote.isSubmitting}
                    goBackButton={this.reInitRenewal(verisk.isLoading || quote.isSubmitting || quote.isCalculating)}
                    i18n={i18n}/>
                </Col>
              </Row>
            )}

            {editMode && type === 'additional' && (
              <Row>
                <Col xs={12}>
                  <ChangeAdditionalOptions
                    product={product}
                    actions={actions}
                    goBackButton={this.reInitRenewal(verisk.isLoading || quote.isSubmitting || quote.isCalculating)}
                    isLoading={quote.isCalculating || quote.isSubmitting}
                  />
                </Col>
              </Row>
            )}
          </>
        </Form>

        <div style={{ marginTop: '15px' }}/>
        <WhatsCovered/>

        <div className="btn-bar btn-bar--auto-height">
          {(this.state.customerLapsed) &&
            <Button
              bsStyle="primary"
              className="pull-right medium-width functional-button"
              onClick={this.unlapseRenewal(quote.application)}
              disabled={quote.isSubmitting}>UNCANCEL RENEWAL
            </Button>
          }

          <CancelRenewalModal {...this.props} toggleCustomerLapsed={this.toggleCustomerLapsed}
                              customerLapsed={this.state.customerLapsed} selectedQuote={formValues}/>
          {!editMode && (
            <>
              <div style={{ marginTop: '15px' }}/>
              <div style={{ display: 'flex' }}>
                <div style={{ flex: '1' }}>
                  <GoBack
                    history={history}
                    url={goBackUrl}
                    push={actions.push}
                    buttonStyle={isMobile ? { width: '175px', fontSize: '12px' } : { width: '250px', fontSize: '14px' }}
                  />
                </div>
                {showCancelRenewal && (
                  <div style={{ flex: '1' }}>
                    <RenewalCancelButton
                      policyId={formValues.renewingPolicyId}
                      controlGroup={controlGroup}
                      buttonStyle={isMobile ? { width: '175px', fontSize: '12px' } : {
                        width: '250px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          <div className="clearfix"/>
        </div>
      </div>
    )
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: true,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');
    return component;
  },
  connect(
    (state: IStore) => {
      const synchronousError = getFormSyncErrors('quote')(state);
      const formValues = getFormValues('quote')(state);
      const region = selector(state, 'region');
      const errorMessage = state.quote.errorMessage;

      return {
        initialValues: state.quote.application,
        auth: state.auth,
        i18n: state.branding.i18n,
        quote: state.quote,
        verisk: state.verisk,
        branding: state.branding,
        customerMTAOptions: get(state.branding.channel.products[0], 'metadata.direct_customer_mta_options', null),
        region,
        formValues,
        synchronousError,
        errorMessage
      };
    },
    (dispatch) => ({
      dispatch,
      actions: {
        push: bindActionCreators(push, dispatch),
        auth: bindActionCreators({ ...authActions }, dispatch),
        destroy: bindActionCreators(destroy, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        branding: bindActionCreators({ ...brandingActions }, dispatch),
        reset: bindActionCreators(reset, dispatch),
        change: bindActionCreators(change, dispatch),
      },
    }),
  ),
])(RenewalForm);
